<!-- src/app/image-library/image-library.component.html -->
<div class="keep-shell image-shell">
  <!-- HEADER / SEARCH --------------------------------------------------- -->
  <header class="keep-header">
    <div class="brand">
      <i class="fa-regular fa-image brand-icon"></i>
      <span class="page-title">Image Library</span>
    </div>

    <form [formGroup]="searchForm" class="search">
      <i class="fa fa-search search-icon"></i>
      <input
        formControlName="query"
        placeholder="Search title, label, or any text…"
      />
    </form>

    <div class="header-actions">
      <button
        type="button"
        class="header-btn"
        (click)="toggleArchivedView()"
      >
        <i
          class="fa"
          [ngClass]="showArchived ? 'fa-box-open' : 'fa-box-archive'"
        ></i>
        <span>{{ showArchived ? 'Show active' : 'Show archived' }}</span>
      </button>
    </div>
  </header>

  <!-- LABEL FILTERS ----------------------------------------------------- -->
  <section class="labels" *ngIf="labels.length">
    <button
      type="button"
      class="label-chip-btn"
      [class.active]="!selectedLabel"
      (click)="selectLabel(null)"
    >
      All
    </button>

    <button
      type="button"
      class="label-chip-btn"
      *ngFor="let label of labels"
      [class.active]="label === selectedLabel"
      (click)="selectLabel(label)"
    >
      {{ label }}
    </button>
  </section>

  <!-- NEW IMAGE INPUT --------------------------------------------------- -->
  <section class="new-image">
    <form [formGroup]="imageForm" (ngSubmit)="createImage()">
      <div class="new-image-card" [style.backgroundColor]="uploadCardColor">
        <div class="new-image-main">
          <input
            type="text"
            class="title"
            placeholder="Image title (e.g. UX mock #1)"
            formControlName="title"
          />

          <textarea
            class="description"
            placeholder="Notes or context about this image…"
            formControlName="description"
            rows="3"
          ></textarea>

          <div class="meta-row">
            <div class="field-group">
              <label>Primary category</label>
              <select formControlName="primaryCategory">
                <option *ngFor="let c of primaryCategories" [value]="c">
                  {{ c }}
                </option>
              </select>
            </div>

            <div class="field-group">
              <label>Labels</label>
              <input
                type="text"
                placeholder="Comma-separated labels (e.g. UI, Dashboard)"
                formControlName="extraLabels"
              />
            </div>
          </div>
        </div>

        <!-- RIGHT SIDE: FILE PICKER + CONTROLS -------------------------- -->
        <div class="new-image-side">
          <div class="file-row">
            <label class="file-input-button">
              <input
                type="file"
                (change)="onFileSelected($event)"
                accept="image/*"
                hidden
              />
              <i class="fa fa-upload"></i>
              <span>
                {{ selectedFile ? 'Change image' : 'Attach image' }}
              </span>
            </label>

            <div class="file-preview" *ngIf="selectedFile">
              <div class="file-thumb">
                <div class="file-thumb-box">
                  {{ selectedFileExt }}
                </div>
              </div>
              <div class="file-info">
                <div class="file-name" [title]="selectedFile.name">
                  {{ selectedFile.name }}
                </div>
                <div class="file-size">
                  {{ selectedFileSizeKb }} KB
                </div>
              </div>
            </div>
          </div>

          <div class="controls-row">
            <div class="controls-left">
              <button
                type="button"
                class="icon-toggle"
                [class.active]="imageForm.value.pinned"
                (click)="imageForm.patchValue({ pinned: !imageForm.value.pinned })"
                title="Pin this image"
              >
                <i class="fa fa-thumbtack"></i>
              </button>

              <div class="color-dots">
                <button
                  type="button"
                  *ngFor="let c of colorPalette"
                  class="color-dot"
                  [style.backgroundColor]="c"
                  [class.selected]="imageForm.value.color === c"
                  (click)="imageForm.patchValue({ color: c })"
                ></button>
              </div>
            </div>

            <div class="controls-right">
              <button
                type="submit"
                class="primary-btn"
                [disabled]="!selectedFile || imageForm.invalid || isSubmitting"
              >
                {{ isSubmitting ? 'Uploading…' : 'Upload image' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  </section>

  <!-- MAIN GRID --------------------------------------------------------- -->
  <div class="content">
    <div class="content-main">
      <!-- PINNED IMAGES ------------------------------------------------- -->
      <section class="image-section" *ngIf="pinnedImages.length">
        <div class="section__header">
          <h3 class="section__title">Pinned images</h3>
          <span class="section__count">{{ pinnedImages.length }}</span>
        </div>

        <div class="image-grid">
          <article
            class="image-card"
            *ngFor="let item of pinnedImages"
            [style.backgroundColor]="item.color || defaultCardColor"
          >
            <div class="image-thumb-wrapper">
              <img
                [src]="item.imageUrl"
                [alt]="item.title"
                class="image-thumb"
              />
            </div>

            <header class="image-card__header">
              <div class="image-card__title-block">
                <h4 class="image-title">
                  {{ item.title || 'Untitled image' }}
                </h4>
                <span class="image-category">
                  {{ item.primaryCategory || 'Uncategorized' }}
                </span>
              </div>

              <div class="image-card__header-actions">
                <button
                  type="button"
                  class="icon-button"
                  title="Unpin"
                  (click)="togglePinned(item, false)"
                >
                  <i class="fa-solid fa-thumbtack"></i>
                </button>
                <button
                  type="button"
                  class="icon-button"
                  title="Archive / restore"
                  (click)="toggleArchived(item)"
                >
                  <i
                    class="fa-solid"
                    [ngClass]="item.archived ? 'fa-box-open' : 'fa-box-archive'"
                  ></i>
                </button>
              </div>
            </header>

            <div class="image-card__body">
              <p class="image-description" *ngIf="item.description">
                {{ item.description }}
              </p>

              <div class="image-labels" *ngIf="item.labels?.length">
                <span
                  class="label-chip"
                  *ngFor="let lbl of item.labels"
                  (click)="selectLabel(lbl)"
                  [class.active]="lbl === selectedLabel"
                >
                  {{ lbl }}
                </span>
              </div>
            </div>

            <footer class="image-card__footer">
              <div class="image-meta">
                <span class="date">
                  {{ item.createdAt | date: 'short' }}
                </span>
                <span class="file-type">
                  {{ item.fileType | uppercase }}
                </span>
              </div>

              <div class="image-actions">
                <button
                  type="button"
                  class="text-button"
                  (click)="openImage(item)"
                >
                  Open image
                </button>

                <button
                  type="button"
                  class="text-button"
                  (click)="useInClarichat(item)"
                >
                  Use in Clarichat
                </button>

                <div class="color-dots">
                  <button
                    type="button"
                    *ngFor="let c of colorPalette"
                    class="color-dot"
                    [style.backgroundColor]="c"
                    [class.selected]="item.color === c"
                    (click)="changeColor(item, c)"
                  ></button>
                </div>

                <button
                  type="button"
                  class="text-button text-button--danger"
                  (click)="delete(item)"
                >
                  Delete
                </button>
              </div>
            </footer>
          </article>
        </div>
      </section>

      <!-- OTHER IMAGES -------------------------------------------------- -->
      <section class="image-section" *ngIf="otherImages.length">
        <div class="section__header">
          <h3 class="section__title">All images</h3>
          <span class="section__count">{{ otherImages.length }}</span>
        </div>

        <div class="image-grid">
          <article
            class="image-card"
            *ngFor="let item of otherImages"
            [style.backgroundColor]="item.color || defaultCardColor"
          >
            <div class="image-thumb-wrapper">
              <img
                [src]="item.imageUrl"
                [alt]="item.title"
                class="image-thumb"
              />
            </div>

            <header class="image-card__header">
              <div class="image-card__title-block">
                <h4 class="image-title">
                  {{ item.title || 'Untitled image' }}
                </h4>
                <span class="image-category">
                  {{ item.primaryCategory || 'Uncategorized' }}
                </span>
              </div>

              <div class="image-card__header-actions">
                <button
                  type="button"
                  class="icon-button"
                  title="Pin"
                  (click)="togglePinned(item, true)"
                >
                  <i class="fa-regular fa-thumbtack"></i>
                </button>
                <button
                  type="button"
                  class="icon-button"
                  title="Archive / restore"
                  (click)="toggleArchived(item)"
                >
                  <i
                    class="fa-solid"
                    [ngClass]="item.archived ? 'fa-box-open' : 'fa-box-archive'"
                  ></i>
                </button>
              </div>
            </header>

            <div class="image-card__body">
              <p class="image-description" *ngIf="item.description">
                {{ item.description }}
              </p>

              <div class="image-labels" *ngIf="item.labels?.length">
                <span
                  class="label-chip"
                  *ngFor="let lbl of item.labels"
                  (click)="selectLabel(lbl)"
                  [class.active]="lbl === selectedLabel"
                >
                  {{ lbl }}
                </span>
              </div>
            </div>

            <footer class="image-card__footer">
              <div class="image-meta">
                <span class="date">
                  {{ item.createdAt | date: 'short' }}
                </span>
                <span class="file-type">
                  {{ item.fileType | uppercase }}
                </span>
              </div>

              <div class="image-actions">
                <button
                  type="button"
                  class="text-button"
                  (click)="openImage(item)"
                >
                  Open image
                </button>

                <button
                  type="button"
                  class="text-button"
                  (click)="useInClarichat(item)"
                >
                  Use in Clarichat
                </button>

                <div class="color-dots">
                  <button
                    type="button"
                    *ngFor="let c of colorPalette"
                    class="color-dot"
                    [style.backgroundColor]="c"
                    [class.selected]="item.color === c"
                    (click)="changeColor(item, c)"
                  ></button>
                </div>

                <button
                  type="button"
                  class="text-button text-button--danger"
                  (click)="delete(item)"
                >
                  Delete
                </button>
              </div>
            </footer>
          </article>
        </div>
      </section>

      <section
        class="image-section"
        *ngIf="!pinnedImages.length && !otherImages.length && !showArchived"
      >
        <p>No images in your library yet. Upload one above to get started.</p>
      </section>
    </div>
  </div>
</div>
